/* eslint-disable i18next/no-literal-string */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  BackButton,
  Footer,
  Page,
} from "@/components/page.tsx";
import { Trans, useTranslation } from "react-i18next";
import { getShopFromLocalStorage, shopQuery, useShop } from "@/hooks/use-shop";
import { PopupModal } from "@/components/popup.tsx";
import { cn, formatCurrency, resetKiosk } from "@/lib/utils";
import { setLanguage, useLanguage } from "@/hooks/use-language";
import { getFeatureFlags, useFeatureFlags } from "@/lib/runtime-config";
import { refreshDynamicFeatureFlags } from "@/lib/dynamic-feature-flags";
import type { CSSProperties, ReactNode } from "react";
import { useMuiModal } from "@/components/modal";
import { WeAreClosedModal } from "@/components/we-are-closed-modal";
import { api, type OpeningHours } from "@/lib/server";
import { TZDate } from "@date-fns/tz";
import { format } from "date-fns";
import { useUser } from "@/hooks/use-user";
import { useQuery } from "@tanstack/react-query";
import { LanguagePicker } from "@/components/language-picker";
import { StartScreenSyncLoadingScreen } from "@/features/start-screen/start-screen-sync-loading-screen";
import {
  getSyncProgress,
  shouldShowSyncLoadingScreen,
  type StartScreenSyncProgress,
  type SyncStatusAllResponse,
} from "@/features/start-screen/start-screen-sync-progress";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { getCashAgentUrl } from "@/lib/runtime-config";
import {
  AudioSlot,
  kioskAudioManifestQueryKey,
} from "@/features/audio/kiosk-audio";

const START_SCREEN_MIN_HEIGHT = 1280;
const START_SCREEN_MAX_HEIGHT = 1920;

export const Route = createFileRoute("/start-screen")({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    await resetKiosk();
    setLanguage(
      getFeatureFlags()?.language ?? getShopFromLocalStorage()?.locale ?? "hu",
    );
    context.queryClient.invalidateQueries({
      queryKey: ["cash-payment-status"],
    });
    // Pick up clips uploaded since the last session.
    context.queryClient.invalidateQueries({
      queryKey: kioskAudioManifestQueryKey,
    });
  },
});

const TIMEZONE_REGION_MAP: Record<string, string> = {
  hu: "Europe/Budapest",
  default: "Europe/Budapest",
};

function checkShopAvailability(hours: OpeningHours): boolean {
  const timeZone =
    TIMEZONE_REGION_MAP[hours.regionCode] ?? TIMEZONE_REGION_MAP.default;
  const opening = hours.opening === "" ? "00:00" : hours.opening;
  const closing =
    hours.closing === "" || hours.closing === "00:00" ? "24:00" : hours.closing;

  const openingNum = parseInt(opening.replace(":", ""));
  const closingNum = parseInt(closing.replace(":", ""));
  const now = parseInt(format(new TZDate(new Date(), timeZone), "HHmm"));
  return openingNum <= now && now <= closingNum;
}

function useNavigateWithClosedCheck() {
  const navigate = useNavigate();
  const { showModal } = useMuiModal();
  const shop = getShopFromLocalStorage();
  const language = useLanguage();

  return async () => {
    if (shop) {
      try {
        const hours = await api.openingHours(shop.shopId);
        const temp = hours.temporaryClosed;
        const showTemporaryClosedMessage = !!temp && temp.showOnKiosk !== false;
        if (showTemporaryClosedMessage) {
          const message =
            temp.message[language] ||
            temp.message.hu ||
            temp.message.en ||
            temp.message.de ||
            temp.message.sk ||
            null;
          showModal(
            <WeAreClosedModal
              opening={hours.opening}
              closing={hours.closing}
              message={message}
              temporarilyClosed
            />,
          );
          // Message-only closures (blockOperations off) inform but don't block.
          if (temp.blockOperations !== false) {
            return;
          }
        }
        if (!checkShopAvailability(hours)) {
          if (!showTemporaryClosedMessage) {
            showModal(
              <WeAreClosedModal
                opening={hours.opening}
                closing={hours.closing}
              />,
            );
          }
          return;
        }
      } catch {
        // If we can't fetch opening hours, let the user proceed
      }
    }
    navigate({ to: "/login-option-selector-screen" });
  };
}

function RouteComponent() {
  const flags = useFeatureFlags();
  useDynamicFeatureFlagsRefresh();
  const syncProgress = useStartScreenSyncProgress({
    enabled: flags.startScreenSyncLoading,
  });

  if (
    flags.startScreenSyncLoading &&
    shouldShowSyncLoadingScreen(syncProgress)
  ) {
    return <StartScreenSyncLoadingScreen syncProgress={syncProgress} />;
  }

  return <NewStartScreen />;
}

/**
 * Refresh the backend feature flags whenever the kiosk lands on the start screen
 * (i.e. on boot and between sessions). When offline the fetch just fails and the
 * last-known cached flags — applied at boot — stay in effect.
 */
function useDynamicFeatureFlagsRefresh() {
  useQuery({
    queryKey: ["dynamic-feature-flags"],
    queryFn: ({ signal }) => refreshDynamicFeatureFlags(signal),
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

function useStartScreenSyncProgress({
  enabled,
}: {
  enabled: boolean;
}): StartScreenSyncProgress {
  const networkStatus = useNetworkStatus();
  const backendDisconnected = networkStatus.data === "disconnected";
  const query = useQuery({
    queryKey: ["debug-sync-status"],
    queryFn: fetchSyncStatus,
    // Stop polling once the gate opens, whether by finishing or by proceeding
    // with local data. Otherwise a stalled sync that resumes would clear the
    // stall flag and re-mount the loading screen until it is fully ready.
    refetchInterval: (query) =>
      !enabled ||
      !shouldShowSyncLoadingScreen(
        getSyncProgress(query.state.data, { backendDisconnected }),
      )
        ? false
        : 1000,
    enabled,
    gcTime: 0,
    retry: false,
    staleTime: 0,
  });

  return {
    ...getSyncProgress(query.data, { backendDisconnected }),
    isFetching: query.isFetching,
    isError: query.isError,
  };
}

async function fetchSyncStatus(): Promise<SyncStatusAllResponse> {
  const res = await fetch(`${getCashAgentUrl()}/debug/sync-status/all`);
  if (!res.ok) throw new Error("sync status fetch failed");
  return (await res.json()) as SyncStatusAllResponse;
}

function NewStartScreen() {
  useTranslation();
  const { data: shop } = useShop();
  const language = useLanguage();
  const handleStart = useNavigateWithClosedCheck();
  const startScreenScaleStyles = getStartScreenScaleStyles(language === "de");
  const supportPhone = getFeatureFlags().supportPhone;

  return (
    <Page className="bg-white flex-col flex" skipInactvityModal>
      <AudioSlot slot="page.start-screen" />
      <div className="flex flex-1 flex-col" style={startScreenScaleStyles}>
        <Header2 variant="small" hideLanguagePicker showUserDetails={false}>
          <img
            src={`/bubbles-logo-on-dark.svg`}
            className="h-16 w-auto mx-auto mb-4"
            style={{ marginTop: "var(--start-screen-logo-margin-top)" }}
          />
          <h1
            className="text-white text-center px-18 leading-16"
            style={{ fontSize: "var(--start-screen-title-font-size)" }}
          >
            <Trans>Üdvözlünk a Bubbles önkiszolgáló mosodában!</Trans>
          </h1>
        </Header2>

        {/* Main Content */}
        <div className="flex-1 flex flex-col h-full">
          <div className="px-8 mt-auto mb-auto">
            {/* Section Title */}
            <div className="text-center mb-3">
              <h2
                className={cn(
                  "text-[#005F87] text-[48px] font-bold font-fancy leading-tight",
                  {
                    "text-[36px]": language === "de",
                  },
                )}
              >
                <Trans>Gépindítás menete</Trans>
              </h2>
            </div>

            <div
              className="mb-4 px-4 flex flex-col"
              style={{ gap: "var(--start-screen-step-gap)" }}
            >
              <StepCard
                number={1}
                opacity={0.1}
                icon="/assets/images/icon-load-machine.svg"
              >
                <StepTitle>
                  <Trans>
                    Pakolj be a gépbe és jegyezd meg a felette lévő számot!
                  </Trans>
                </StepTitle>
                <StepDescription>
                  <Trans>
                    Ne tömd túl a gépet, mert az a mosás / szárítás minőségének
                    rovására megy!
                  </Trans>
                </StepDescription>
              </StepCard>

              <StepCard
                number={2}
                opacity={0.18}
                icon="/assets/images/icon-close-door.svg"
              >
                <StepTitle>
                  <Trans>Csukd be a gép ajtaját!</Trans>
                </StepTitle>
                <StepDescription>
                  <Trans>
                    A mosógép kijelzőjén, ha a jobb alsó sarokban megjelenik az
                    OPEN DOOR felirat, akkor zárt be sikeresen. Szárítógép
                    esetén az ajtó minimális emelése segíthet a pontos
                    záródásban.
                  </Trans>
                </StepDescription>
              </StepCard>

              <StepCard
                number={3}
                opacity={0.25}
                icon="/assets/images/icon-select-program.svg"
              >
                <StepTitle>
                  <Trans>
                    Válassz programot a mosógépen a SELECT PROGRAM felirat
                    megnyomásával, vagy a szárítógépen a +/- gombok
                    segítségével!
                  </Trans>
                </StepTitle>
                <StepDescription>
                  <Trans>
                    A gép kifizetését követően már nincs lehetőség a program és
                    a hőfok módosítására!
                  </Trans>
                </StepDescription>
              </StepCard>

              <StepCard
                number={4}
                opacity={0.32}
                icon="/assets/images/icon-pay-here.svg"
              >
                <StepTitle>
                  <Trans>
                    Fizess itt, a kioszknál! Ügyelj arra, hogy a megfelelő
                    sorszámú gépet fizesd ki!
                  </Trans>
                </StepTitle>
                <StepDescription>
                  <Trans>
                    Regisztrált felhasználók azonnali, állandó kedvezményben
                    részesülnek!
                  </Trans>
                </StepDescription>
              </StepCard>

              <StepCard
                number={5}
                opacity={0.4}
                icon="/assets/images/icon-check-started.svg"
              >
                <StepTitle>
                  <Trans>Ellenőrizd, hogy elindult-e a gép!</Trans>
                </StepTitle>
                <StepDescription>
                  <Trans>
                    Ha a fizetés 2 percnél tovább tartott, a mosógép programját
                    újra ki kell választani. Ha nem indult el a gép, ellenőrizd,
                    hogy az első 4 pont mindegyike teljesült-e?
                  </Trans>
                </StepDescription>
              </StepCard>
            </div>
          </div>

          <div className="mt-0 mb-auto">
            {/* CTA Button */}
            <div
              className="flex justify-center mt-auto"
              style={{ marginBottom: "var(--start-screen-cta-margin-bottom)" }}
            >
              <button
                onClick={handleStart}
                className={cn(
                  "bg-[#F16541] px-14 py-3 text-[42px] font-extrabold text-white rounded-full font-helvetica shadow-lg active:scale-95 transition-transform",
                  "pt-5",
                  {
                    "text-[32px]": language === "de",
                  },
                )}
              >
                <Trans>Vágjunk bele!</Trans>
              </button>
            </div>

            {/* Language Selector */}
            <div className="text-center mb-3">
              <p className="text-[#005F87] text-[28px] font-bold mb-2 tracking-wide">
                Nyelv / Language / Sprache / Jazyk
              </p>
              <div className="flex justify-center gap-4">
                {(["hu", "en", "de", "sk"] as const).map((code) => (
                  <img
                    key={code}
                    src={`/assets/images/flag-${code}-${language === code ? "active" : "inactive"}.svg`}
                    className="w-[120px] active:scale-95 transition-transform"
                    onClick={() => setLanguage(code)}
                    alt={code.toUpperCase()}
                  />
                ))}
              </div>
            </div>

            {/* Help Line */}
            <p
              className="text-center text-[#005F87] text-[24px] font-bold pb-4 tracking-wide"
              style={{ marginTop: "var(--start-screen-help-margin-top)" }}
            >
              <Trans values={{ supportPhone }}>
                SEGÍTSÉG 0-24: {{ supportPhone }}
              </Trans>
            </p>
          </div>
        </div>

        {shop && <PopupModal />}
        <div className="opacity-0">
          <Footer />
        </div>
      </div>
    </Page>
  );
}

function StepTitle({ children }: { children: ReactNode }) {
  return (
    <p
      className="font-bold text-[#005F87] leading-snug"
      style={{ fontSize: "var(--start-screen-step-title-font-size)" }}
    >
      {children}
    </p>
  );
}

function StepDescription({ children }: { children: ReactNode }) {
  return (
    <p
      className="text-[#005F87] leading-snug mt-0.5"
      style={{ fontSize: "var(--start-screen-step-description-font-size)" }}
    >
      {children}
    </p>
  );
}

function StepCard({
  number,
  opacity,
  icon,
  children,
}: {
  number: number;
  opacity: number;
  icon: string;
  children: ReactNode;
}) {
  return (
    <div
      className="flex items-center gap-3 rounded-2xl px-4 py-2.5 relative"
      style={{ backgroundColor: `rgba(55, 192, 205, ${opacity})` }}
    >
      <div className="flex-shrink-0 size-14 rounded-full flex items-center justify-center absolute -left-[1.6rem] bg-[#F16541] border-white border-2">
        <span className="text-white font-bold text-[32px] font-fancy leading-none mt-[6px]">
          {number}
        </span>
      </div>
      <img src={icon} className="flex-shrink-0 ml-7 w-16 h-16" alt="" />
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}

// center vertically header
function Header2({
  backArrowHref,
  backArrowTitle = "Vissza",
  variant = "large",
  showUserDetails = true,
  children,
  params,
  hideLanguagePicker,
}: {
  backArrowHref?: string | ReactNode;
  backArrowTitle?: string;
  variant?: "small" | "medium" | "large";
  showUserDetails?: boolean;
  children?: ReactNode;
  params?: Record<string, string>;
  hideLanguagePicker?: boolean;
}) {
  const { t } = useTranslation();
  const search = Route.useSearch({});
  const navigate = Route.useNavigate();
  const { data: user } = useUser();
  const { data: shop } = useQuery(shopQuery(search.kioskKey));
  const lang = useLanguage();

  return (
    <>
      <header
        className={cn(
          `relative bg-[url('/wave.svg')] bg-no-repeat bg-cover bg-bottom pt-8 w-full h-[33vh]`,
          {
            "h-[20vh]": variant === "small",
            "h-[25vh]": variant === "medium",
          },
        )}
      >
        <div
          className={cn(
            `w-full flex items-start justify-between px-8 absolute`,
            {
              "justify-end": !user && !backArrowHref,
            },
          )}
        >
          <div>
            {typeof backArrowHref === "string" ? (
              <BackButton
                label={t(backArrowTitle)}
                onClick={() => {
                  navigate({
                    href: backArrowHref,
                    params: params as any,
                    reloadDocument: true,
                  });
                }}
              />
            ) : (
              backArrowHref
            )}
            {user && showUserDetails && (
              <div className="space-y-8">
                <p className="flex gap-3 items-center text-5xl font-helvetica">
                  <img src="/user-avatar.svg" className="size-16 mb-2" />
                  <span className={`text-white`}>
                    {lang === "hu" ? (
                      <>
                        {user.lastName} {user.firstName}
                      </>
                    ) : (
                      <>
                        {user.firstName} {user.lastName}
                      </>
                    )}
                  </span>
                </p>

                <p className="flex gap-3 items-center text-5xl font-helvetica">
                  <img src="/wallet-white.svg" className="size-16 mb-2" />
                  <span className={`text-white`}>
                    {formatCurrency(user.balance, shop?.currencyCode ?? "HUF")}
                  </span>
                </p>
              </div>
            )}
          </div>
          {!hideLanguagePicker && <LanguagePicker />}
        </div>
        {children}
      </header>
    </>
  );
}

type StartScreenScaleStyles = CSSProperties & {
  "--start-screen-logo-margin-top": string;
  "--start-screen-title-font-size": string;
  "--start-screen-step-gap": string;
  "--start-screen-step-title-font-size": string;
  "--start-screen-step-description-font-size": string;
  "--start-screen-cta-margin-bottom": string;
  "--start-screen-help-margin-top": string;
};

function scaleByViewportHeight(min: number, max: number) {
  const delta = max - min;
  const viewportUnit =
    (delta * 100) / (START_SCREEN_MAX_HEIGHT - START_SCREEN_MIN_HEIGHT);
  const intercept =
    min -
    (delta * START_SCREEN_MIN_HEIGHT) /
      (START_SCREEN_MAX_HEIGHT - START_SCREEN_MIN_HEIGHT);

  return `clamp(${min}px, calc(${intercept}px + ${viewportUnit}dvh), ${max}px)`;
}

function getStartScreenScaleStyles(
  isGermanTitle: boolean,
): StartScreenScaleStyles {
  return {
    "--start-screen-logo-margin-top": scaleByViewportHeight(0, 48),
    "--start-screen-title-font-size": scaleByViewportHeight(
      isGermanTitle ? 36 : 52,
      52,
    ),
    "--start-screen-step-gap": scaleByViewportHeight(8, 32),
    "--start-screen-step-title-font-size": scaleByViewportHeight(22, 26),
    "--start-screen-step-description-font-size": scaleByViewportHeight(20, 24),
    "--start-screen-cta-margin-bottom": scaleByViewportHeight(16, 96),
    "--start-screen-help-margin-top": scaleByViewportHeight(16, 96),
  };
}
